import {
  Box,
  Button,
  Card,
  CardContent,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useCallback } from 'react';
import { isNullish } from 'remeda';
import { DynamicIcon } from 'src/components';
import { useResponsiveCardStyles } from 'src/components/SidePanelTabs/shared';
import { StyledCardHeader } from 'src/components/SidePanelTabs/SiteClient/client-card';
import {
  Address,
  Conversation,
  mapProject,
  PartnerGroupRules,
  PartnerSetting,
  usePartnerGroupRules,
  usePartnerSettings,
} from 'src/graphql';
import { Modals, useModalMethods } from 'src/store/modals';
import { ProjectCard } from './project-card';

interface ProjectsProps {
  address: Partial<Address>;
  conversation?: Conversation;
}

export const ProjectCardSkeleton = () => (
  <Card
    sx={{
      border: '1px solid',
      borderColor: (theme) => theme.palette.custom.OV.sys.outlineVariant,
      marginTop: (theme) => theme.spacing(2),
    }}
  >
    <StyledCardHeader
      title={
        <Stack direction="row" alignItems="center" gap={2}>
          <Skeleton width="40%" />
          <Skeleton variant="circular" width={20} height={20} />
        </Stack>
      }
      action={
        <Stack direction="row" alignItems="center" gap={2}>
          <Skeleton variant="circular" width={20} height={20} />
          <Skeleton variant="circular" width={20} height={20} />
        </Stack>
      }
    />
    <CardContent
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        paddingBottom: (theme) => theme.utils.important(theme.spacing(2)),
      }}
    >
      {[1, 2, 3, 4].map((item) => (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
          key={item}
        >
          <Skeleton width="40%" height={20} />
          <Skeleton width="55%" />
        </Stack>
      ))}
    </CardContent>
  </Card>
);

export const Projects = ({ conversation, address }: ProjectsProps) => {
  const theme = useTheme();

  const { containerRef, containerWidth, responsiveCardStyles } = useResponsiveCardStyles();

  const { partnerGroupRules, loading: loadingPartnerGroupRules } = usePartnerGroupRules(
    address.partner?.id ?? address?.partnerId ?? conversation?.partner?.id,
  );
  const { showModal: openAddProjectModal } = useModalMethods(Modals.AddProject);
  const { data: disabledProvisionProjects, loading: loadingPartnerSettings } =
    usePartnerSettings({
      partnerSetting: PartnerSetting.DisabledProvisionProjects,
      partnerId: address.partnerId,
      splitPartnerAndPlatformData: true,
    });

  const hasAccess =
    partnerGroupRules[PartnerGroupRules.AccessToProvisionProjects] &&
    disabledProvisionProjects.partnerLevel?.[0]?.toLowerCase() !== 'true';

  const loading = loadingPartnerGroupRules || loadingPartnerSettings;

  const handleAddProjectClick = useCallback(() => {
    openAddProjectModal({ address: address as Address });
  }, [address, openAddProjectModal]);

  if (!hasAccess) {
    return (
      <Stack alignItems="center" justifyContent="center" padding={2}>
        <DynamicIcon
          name="alert-circle"
          library="lucide"
          size={32}
          color={theme.palette.error.main}
          sx={{ cursor: 'default' }}
        />
        <Typography
          variant="body1"
          color="textSecondary"
          textAlign="center"
          mt={1}
          sx={{ whiteSpace: 'normal', maxWidth: '80%' }}
        >
          Partner does not have access to Provision Projects
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack ref={containerRef}>
      <Stack direction="column" justifyContent="flex-end">
        <Button
          sx={{ alignSelf: 'flex-end', width: 'fit-content' }}
          onClick={handleAddProjectClick}
          disabled={!!address?.isDeleted}
        >
          + Add Project
        </Button>
      </Stack>
      {address?.projects?.length === 0 && (
        <Stack alignItems="center" justifyContent="center" padding={2}>
          <Typography variant="body1" color="textSecondary">
            No projects found
          </Typography>
        </Stack>
      )}

      {/* Only render content when we have measured the width */}
      {!isNullish(containerWidth) && (
        <Box display="grid" gap={2} sx={responsiveCardStyles}>
          {loading ? (
            <>
              {[1, 2, 3].map((item) => (
                <ProjectCardSkeleton key={item} />
              ))}
            </>
          ) : null}
          {address?.projects?.map((project) => (
            <ProjectCard
              key={project.id}
              address={address as Address}
              project={mapProject(project)}
              disabled={address.isDeleted}
            />
          ))}
        </Box>
      )}
    </Stack>
  );
};
